/**
 * Shared resvg-wasm utility for main-thread SVG → PNG rasterization.
 *
 * Safari rasterizes SVGs loaded via Image() at their declared intrinsic
 * dimensions, so the old Image→Canvas→PNG pipeline produced blurry output
 * when upscaling. resvg-wasm rasterizes directly from SVG to PNG at any
 * target resolution, avoiding browser rendering quirks entirely.
 *
 * Main-thread and worker callers share this implementation, with a separate
 * singleton in each JS context. WASM cannot access browser/system fonts, so
 * explicitly load RID's bundled TTFs for SVG text and family fallback.
 */
import { resizeSvgViewport } from './packages/pdf-export/src/svgAsset';
import { initWasm, Resvg } from '@resvg/resvg-wasm';
import resvgWasmUrl from '@resvg/resvg-wasm/index_bg.wasm?url';

// Singleton init — initWasm() can only be called once per JS context.
let wasmReady = false;
let fontBuffers: Uint8Array[] | null = null;
let wasmInitPromise: Promise<void> | null = null;

export class SvgRendererInitializationError extends Error {
  constructor() {
    super('The artwork renderer could not initialize. Retry, or reload the page and try again.');
    this.name = 'SvgRendererInitializationError';
  }
}

export async function ensureArtworkRendererReady(): Promise<void> {
  try { await ensureResvgReady(); }
  catch { throw new SvgRendererInitializationError(); }
}
export function resolveSvgColor(value: string): { red: number; green: number; blue: number; opacity: number } {
  const escaped = value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const renderer = new Resvg(`<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"><rect width="1" height="1" fill="${escaped}"/></svg>`);
  const rendered = renderer.render();
  try {
    const [red, green, blue, alpha] = rendered.pixels;
    return { red: alpha ? red / alpha : 0, green: alpha ? green / alpha : 0,
      blue: alpha ? blue / alpha : 0, opacity: alpha / 255 };
  } finally {
    rendered.free();
    renderer.free();
  }
}

export function ensureResvgReady(): Promise<void> {
  if (wasmReady && fontBuffers) return Promise.resolve();
  if (wasmInitPromise) return wasmInitPromise;

  const initPromise = (async () => {
    if (!wasmReady) {
      await initWasm(fetch(resvgWasmUrl));
      wasmReady = true;
    }
    const buffers = await Promise.all([
      'RobotoCondensed-Variable.ttf',
      'RobotoCondensed-Italic-Variable.ttf',
    ].map(async (filename) => {
      const response = await fetch(`${import.meta.env.BASE_URL}fonts/${filename}`);
      if (!response.ok) throw new SvgRendererInitializationError();
      const bytes = new Uint8Array(await response.arrayBuffer());
      // Reject empty/HTML responses (including a SPA fallback on a missing URL).
      const isTtf = bytes.length >= 12 && bytes[0] === 0 && bytes[1] === 1 && bytes[2] === 0 && bytes[3] === 0;
      if (!isTtf) throw new SvgRendererInitializationError();
      return bytes;
    }));
    fontBuffers = buffers;
  })();

  wasmInitPromise = initPromise;
  void initPromise.catch(() => {
    if (wasmInitPromise === initPromise) {
      wasmInitPromise = null;
    }
  });
  return initPromise;
}

/** Shared font options for the source probe and every fitted artwork renderer. */
function rendererOptions() {
  if (!wasmReady || !fontBuffers) throw new SvgRendererInitializationError();
  return {
    font: {
      fontBuffers,
      defaultFontFamily: 'Roboto Condensed',
      sansSerifFamily: 'Roboto Condensed',
      serifFamily: 'Roboto Condensed',
      monospaceFamily: 'Roboto Condensed',
      cursiveFamily: 'Roboto Condensed',
      fantasyFamily: 'Roboto Condensed',
    },
  };
}

export type SvgRasterFit = 'contain' | 'cover' | 'fill';

export type SvgRaster = { pngBytes: Uint8Array; intrinsicWidth: number; intrinsicHeight: number };

/** Read only the trusted canonical root emitted by the existing probe.
 * WASM width/height getters are integer raster dimensions, not precise viewports.
 * Keep the uploaded document untouched for rendering; do not infer ink bounds.
 */
function normalizedViewport(probe: InstanceType<typeof Resvg>): { width: number; height: number } {
  const root = /^<svg(?=\s|>)([^>]*)>/.exec(probe.toString())?.[1];
  const number = /^[+]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;
  const read = (axis: 'width' | 'height') => {
    const value = root?.match(new RegExp(`(?:^|\\s)${axis}="([^"]*)"(?:\\s|$)`))?.[1];
    return value && number.test(value) ? Number(value) : Number.NaN;
  };
  const width = read('width'), height = read('height');
  if (![width, height].every(value => Number.isFinite(value) && value > 0)) {
    throw new Error('SVG artwork dimensions are invalid.');
  }
  return { width, height };
}

/** Raster plus the same probe's normalized source viewport; native width-only options are preserved. */
export function svgToPngRaster(svgString: string, fitWidth?: number, fitHeight?: number, fit: SvgRasterFit = 'fill'): SvgRaster {
  const opts = rendererOptions();
  // Native PDF callers retain the original fitted width-only render and errors.
  // Metadata comes from that same probe; the two-axis admission gate is separate.
  if (fitHeight === undefined) {
    const probe = new Resvg(svgString, { ...opts, ...(fitWidth ? { fitTo: { mode: 'width' as const, value: fitWidth } } : {}) });
    let raster: ReturnType<typeof probe.render> | undefined;
    try {
      const { width, height } = normalizedViewport(probe);
      raster = probe.render();
      return { pngBytes: raster.asPng(), intrinsicWidth: width, intrinsicHeight: height };
    } finally { raster?.free(); probe.free(); }
  }
  if (typeof fitWidth !== 'number' || !Number.isFinite(fitWidth) || fitWidth <= 0 || !Number.isFinite(fitHeight) || fitHeight <= 0) {
    throw new Error('SVG target dimensions are invalid.');
  }
  const outputWidth = Math.ceil(fitWidth), outputHeight = Math.ceil(fitHeight);
  if (fit !== 'contain' && (outputWidth > 4096 || outputHeight > 4096 || outputWidth * outputHeight > 16777216)) {
    throw new Error('SVG artwork exceeds safe raster dimensions.');
  }
  let resvg = new Resvg(svgString, opts);
  let rendered: ReturnType<typeof resvg.render> | undefined;
  try {
    // Preserve released admission/error policy. Precise metadata must not turn
    // native zero-axis source viewports into newly supported artwork.
    if (![resvg.width, resvg.height].every(value => Number.isFinite(value) && value > 0)) {
      throw new Error('SVG artwork dimensions are invalid.');
    }
    const { width, height } = normalizedViewport(resvg);
    const scale = Math.min(fitWidth / width, fitHeight / height);
    const rasterWidth = fit === 'contain' ? Math.max(1, Math.ceil(width * scale)) : outputWidth;
    const rasterHeight = fit === 'contain' ? Math.max(1, Math.ceil(height * scale)) : outputHeight;
    if (rasterWidth > 4096 || rasterHeight > 4096 || rasterWidth * rasterHeight > 16777216) {
      throw new Error('SVG artwork exceeds safe raster dimensions.');
    }
    const next = fit !== 'contain' || width * scale < 1 || height * scale < 1
      ? new Resvg(resizeSvgViewport(svgString, rasterWidth, rasterHeight, width, height, fit === 'cover' ? 'cover' : 'fill', fit === 'cover' ? { width: fitWidth, height: fitHeight } : undefined), opts)
      : new Resvg(svgString, { ...opts, fitTo: { mode: 'zoom', value: scale } });
    resvg.free(); resvg = next;
    rendered = resvg.render();
    return { pngBytes: rendered.asPng(), intrinsicWidth: width, intrinsicHeight: height };
  } finally {
    rendered?.free(); resvg.free();
  }
}

/** Byte-only API retained for existing preview/ZPL and width-only PDF/QR callers. */
export function svgToPngBytes(svgString: string, fitWidth?: number, fitHeight?: number, fit: SvgRasterFit = 'fill'): Uint8Array {
  if (fitHeight !== undefined) return svgToPngRaster(svgString, fitWidth as number, fitHeight, fit).pngBytes;
  const resvg = new Resvg(svgString, { ...rendererOptions(), ...(fitWidth ? { fitTo: { mode: 'width' as const, value: fitWidth } } : {}) });
  let rendered: ReturnType<typeof resvg.render> | undefined;
  try { rendered = resvg.render(); return rendered.asPng(); }
  finally { rendered?.free(); resvg.free(); }
}

/**
 * Rasterise an SVG string to a data:image/png;base64,… URL.
 * Ensures the WASM module is loaded before rendering.
 */
export async function svgToPngDataUrl(svgString: string, fitWidth?: number): Promise<string> {
  await ensureResvgReady();
  const pngBytes = svgToPngBytes(svgString, fitWidth);

  // Convert Uint8Array → base64 data URL
  let binary = '';
  for (let i = 0; i < pngBytes.length; i++) {
    binary += String.fromCharCode(pngBytes[i]);
  }
  return `data:image/png;base64,${btoa(binary)}`;
}
